import { Card, Tag, Typography, Space, Avatar, theme, Button, Flex, Dropdown, message, Modal, Skeleton } from "antd";
import { ItemType } from "antd/es/menu/interface";
import { ApiService } from "api/api.service";
import { BoardDto, BoardResource } from "dtos/kanban.dto";
import { RoleTypes } from "dtos/user.dto";
import { IconType } from "react-icons/lib";
import {
  TbLayoutKanban,
  TbBriefcase,
  TbCreditCard,
  TbFileText,
  TbUsers,
  TbArrowRight,
  TbColumns3,
  TbDotsVertical,
} from "react-icons/tb";
import { useAppSelector } from "store";

const { Text } = Typography;

export const resourceConfig: Record<
  BoardResource,
  { Icon: IconType; color: string; bg: string; label: string; possibleConnections: string[] }
> = {
  [BoardResource.APPLICATION]: {
    Icon: TbFileText,
    color: "purple",
    bg: "bg-purple-500",
    label: "Application",
    possibleConnections: [BoardResource.CREDITOR],
  },
  [BoardResource.LEAD]: { Icon: TbUsers, color: "pink", bg: "bg-pink-500", label: "Lead", possibleConnections: [] },
  [BoardResource.LOAN]: {
    Icon: TbBriefcase,
    color: "cyan",
    bg: "bg-cyan-500",
    label: "Loan",
    possibleConnections: [BoardResource.CREDITOR, BoardResource.APPLICATION],
  },
  [BoardResource.CREDITOR]: {
    Icon: TbCreditCard,
    color: "green",
    bg: "bg-emerald-500",
    label: "Creditor",
    possibleConnections: [BoardResource.APPLICATION, BoardResource.LOAN],
  },
};

function timeAgo(iso: string | Date | number) {
  const diff = Date.now() - new Date(iso).getTime();
  const d = Math.floor(diff / 86400000);
  if (d === 0) return "Today";
  if (d === 1) return "Yesterday";
  if (d < 30) return `${d}d ago`;
  const m = Math.floor(d / 30);
  if (m < 12) return `${m}mo ago`;
  return `${Math.floor(m / 12)}y ago`;
}

export function BoardCard({
  board,
  onClick,
  refetch,
}: Readonly<{ board: BoardDto; refetch?: () => unknown; onClick: (board: BoardDto) => unknown }>) {
  const isSuperAdmin = useAppSelector((state) => state.user.role) === RoleTypes.SUPERADMIN;
  const { token } = theme.useToken();
  const cfg = resourceConfig[board.resource] ?? resourceConfig[BoardResource.APPLICATION];
  const activeColumns = board.columns.filter((c) => !c.disabled).length;
  const { Icon } = cfg;
  const deleteBoard = ApiService.kanban.useDeleteBoard({ delete_columns: true });
  const copyColumnsConfig = ApiService.kanban.useCopyColumnsConfig();

  const dropdownItems: ItemType[] = [
    {
      key: "copy-config",
      label: "Copy Config",
      onClick() {
        copyColumnsConfig.mutate(board._id, {
          onSuccess() {
            message.success("Successfully copied the board config");
          },
        });
      },
    },
  ];
  if (isSuperAdmin) {
    dropdownItems.push({
      key: "delete",
      label: "Delete Board",
      danger: true,
      onClick() {
        Modal.confirm({
          title: "Delete Board",
          content: (
            <Typography.Paragraph>
              Are you sure you want to delete this board? All columns and related cards will be deleted as well
            </Typography.Paragraph>
          ),
          okText: "Delete",
          okButtonProps: {
            danger: true,
          },
          cancelButtonProps: {
            danger: true,
          },
          onOk() {
            deleteBoard.mutate(board._id, {
              onSuccess() {
                message.success("Board deleted successfully");
              },
              onSettled() {
                refetch?.();
              },
            });
          },
        });
      },
    });
  }

  return (
    <Card
      styles={{ body: { padding: token.paddingMD } }}
      className="border border-gray-200 shadow-sm hover:shadow w-full min-w-[280px] transition-all"
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {/* Header */}
        <Space align="start" style={{ width: "100%", justifyContent: "space-between" }}>
          <Space align="center" size="middle">
            <Avatar shape="square" size={40} className={`${cfg.bg}`} icon={<Icon size={18} />} />
            <Space direction="vertical" size={2}>
              <Text strong ellipsis style={{ fontSize: token.fontSizeLG }}>
                {board.name}
              </Text>
              <Tag color={cfg.color} style={{ marginInlineEnd: 0, height: 20 }}>
                {board.resource}
              </Tag>
            </Space>
          </Space>
          <Dropdown
            menu={{
              items: dropdownItems,
            }}
            className="flex-1 grow"
          >
            <Button size="small" className="p-1 min-w-6">
              <TbDotsVertical size={16} color={token.colorTextTertiary} />
            </Button>
          </Dropdown>
        </Space>

        {/* Stats */}
        <Space size="large">
          <Space size={4}>
            <TbColumns3 size={14} color={token.colorTextSecondary} />
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              <Text strong>{activeColumns}</Text>
              {activeColumns !== board.columns.length && ` / ${board.columns.length}`} columns
            </Text>
          </Space>
          {board.groups && board.groups.length > 0 && (
            <Space size={4}>
              <TbLayoutKanban size={14} color={token.colorTextSecondary} />
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                <Text strong>{board.groups.length}</Text> groups
              </Text>
            </Space>
          )}
        </Space>
      </Space>

      <Flex className="mt-5 items-center justify-between">
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          {/* {timeAgo(board.createdAt)} */}
          View {board.totalCards} cards
        </Text>
        <Button onClick={() => onClick?.(board)} variant="text" type="text">
          {/* <Space className="gap-1">
            <span className="text-xs">View {board.totalCards} cards</span>
          </Space> */}
          <TbArrowRight />
        </Button>
      </Flex>
    </Card>
  );
}

export function BoardCardSkeleton() {
  const { token } = theme.useToken();

  return (
    <Card
      styles={{ body: { padding: token.paddingMD } }}
      className="border border-gray-200 shadow-sm w-full min-w-[280px]"
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Space align="center" size="middle" style={{ width: "100%", justifyContent: "space-between" }}>
          <Space align="center" size="middle">
            <Skeleton.Avatar active shape="square" size={40} />
            <Space direction="vertical" size={4}>
              <Skeleton.Input active size="small" style={{ width: 120, height: 16 }} />
              <Skeleton.Input active size="small" style={{ width: 72, height: 14 }} />
            </Space>
          </Space>
          <Skeleton.Button active size="small" style={{ width: 24, height: 24 }} />
        </Space>

        <Space size="large">
          <Skeleton.Input active size="small" style={{ width: 80, height: 13 }} />
          <Skeleton.Input active size="small" style={{ width: 64, height: 13 }} />
        </Space>
      </Space>

      <Space style={{ marginTop: 20, width: "100%", justifyContent: "space-between" }}>
        <Skeleton.Input active size="small" style={{ width: 88, height: 13 }} />
        <Skeleton.Button active size="small" style={{ width: 24, height: 24 }} />
      </Space>
    </Card>
  );
}
